import BottomSheet, {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetView,
  useBottomSheetInternal,
} from '@gorhom/bottom-sheet';
import { useCallback } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { YStack } from 'tamagui';

import { color, radius, space } from '@/constants/tokens';
import PSButton from './ui/PSButton';
import PSInput from './ui/PSInput';
import PSText from './ui/PSText';

/**
 * Collects the social-media link an approval-only tier requires before the
 * request can be made. Render-only: the screen owns the draft value, runs it
 * through `normalizeSocialLink`, and decides what "submit" means next (store
 * it on the cart, push to confirm) — this sheet just asks and shows the error
 * that comes back.
 *
 * MOUNT IT ONLY WHILE IT SHOULD BE OPEN — there is no `open` prop. See the
 * `index` comment below for why a closed-but-mounted instance is a bug.
 */
export default function SocialLinkSheet({
  onClose,
  link,
  ctaCopy,
  onChangeLink,
  onSubmit,
  error,
  isSubmitting = false,
  copy,
}: {
  ctaCopy: string;
  /** Swipe-down and backdrop-press land here too, so the screen unmounts it. */
  onClose: () => void;
  link: string;
  onChangeLink: (link: string) => void;
  onSubmit: () => void;
  error?: string;
  /** Set while the screen's `onSubmit` is saving the link to the profile. */
  isSubmitting?: boolean;
  copy: string;
}) {
  const { bottom } = useSafeAreaInsets();

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        pressBehavior="close"
      />
    ),
    []
  );

  return (
    <BottomSheet
      // Index 0 IS the mount state, so gorhom's own mount animation opens it —
      // never mounted closed and toggled by ref. A closed-but-mounted sheet
      // seeds its closed position from `Dimensions.get('window').height`, which
      // on mobile web is the KEYBOARD-SHRUNK visualViewport, and `close()`
      // no-ops before the first layout — so it paints itself open the moment
      // the keyboard goes. Content-sized (dynamic sizing), so 0 is the only
      // detent there is.
      index={0}
      onClose={onClose}
      enablePanDownToClose
      keyboardBlurBehavior="restore"
      // No `android_keyboardInputMode="adjustResize"`: edge-to-edge Android
      // never resizes the window, so the sheet must lift itself (adjustPan).
      backdropComponent={renderBackdrop}
      // Raw token values: none of these three props are Tamagui components.
      backgroundStyle={{
        backgroundColor: color.background,
        borderRadius: radius.base,
      }}
      handleIndicatorStyle={{ backgroundColor: color.borderColor }}
    >
      <BottomSheetView>
        <YStack
          px={'$space.lg'}
          pt={'$space.lg'}
          gap={'$space.lg'}
          // Clear of the home indicator; the sheet has no safe area of its own.
          pb={space.lg + bottom}
        >
          <YStack gap={'$xs'}>
            <PSText.H2>Add your social link</PSText.H2>
            <PSText.Body2 color="$textDarkMuted">{copy}</PSText.Body2>
          </YStack>

          <SocialLinkField
            link={link}
            onChangeLink={onChangeLink}
            error={error}
          />

          <PSButton
            fullWidth
            title={ctaCopy}
            loading={isSubmitting}
            loadingTitle="Saving"
            disabled={link.trim().length === 0}
            onPress={onSubmit}
          />
        </YStack>
      </BottomSheetView>
    </BottomSheet>
  );
}

// Any truthy handle does: the sheet only reads `target` to decide whether a
// keyboard event belongs to it, and to clear it again on blur.
const FOCUSED = 1;

/**
 * The field, plus the one thing that makes the sheet rise over the keyboard.
 *
 * gorhom lifts the sheet only for its OWN `BottomSheetTextInput`: that input
 * stamps `target` onto the sheet's keyboard state on focus, and a keyboard
 * event arriving with no target is ignored. This is `PSInput`, so it stamps
 * the target itself from its existing focus/blur callbacks — see
 * `project-gorhom-sheet-input-keyboard` for the full story. Split out because
 * the hook only works inside the sheet.
 *
 * No autofocus: opening the sheet with the keyboard already animating in
 * buried the sheet outright rather than rising above it. The user taps the
 * field themselves, by which point the sheet is long done animating and the
 * keyboard show is a plain, un-raced focus.
 */
function SocialLinkField({
  link,
  onChangeLink,
  error,
}: {
  link: string;
  onChangeLink: (link: string) => void;
  error?: string;
}) {
  const { animatedKeyboardState } = useBottomSheetInternal();

  return (
    <PSInput
      value={link}
      onChangeText={onChangeLink}
      type="url"
      placeholder="https://instagram.com/you"
      error={error}
      onFocus={() =>
        animatedKeyboardState.set((state) => ({ ...state, target: FOCUSED }))
      }
      onBlur={() =>
        animatedKeyboardState.set((state) => ({ ...state, target: undefined }))
      }
    />
  );
}
